import { css } from '@emotion/css';
import { NavLink, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { tKeys, useTranslation } from 'locales';
import { matchPath, useHistory, useLocation } from 'react-router';
import { find, isDefined, isNonNullish, map, pipe } from 'remeda';
import { UpsellFeatureSchema } from '../../Upsell/types';
import type { MenuItem } from '../types';
import { MenuItemBadges } from './MenuItemBadges';

export type NavMenuItemProps = MenuItem;

export const NavMenuItem = ({
  label,
  Icon,
  path,
  pathSuffix,
  unreadCount,
  tagLabel,
  isHidden,
  showUpsell = false,
  onClick,
  onNavigate,
  isExactRouteMatch = true,
}: NavMenuItemProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const theme = useMantineTheme();

  if (isHidden) return null;

  if (!isDefined(path)) {
    return (
      <NavLink
        component="button"
        onClick={onClick}
        className={styles.navLink}
        classNames={{ root: styles.navLinkRoot() }}
        label={label}
        leftSection={<Icon color={theme.colors.purple[5]} stroke="1.7" size={20} />}
      />
    );
  }

  const doesMatchCurrentPath = isNonNullish(
    matchPath(location.pathname, { path, exact: isExactRouteMatch }),
  );

  // Walk path segments from the end to find the first one matching an upsell feature
  // e.g. '/mails/inbox' → tries 'inbox' (invalid), then 'mails' (valid)
  const upsellFeature = pipe(
    path.split('/').reverse(),
    map(s => s.replace(/-/g, '')),
    find(s => UpsellFeatureSchema.safeParse(s).success),
  );
  const isUpsellPath = showUpsell && isNonNullish(upsellFeature);

  const handleClick = () => {
    if (isUpsellPath) {
      history.push(`/upgrade/${upsellFeature}`);
      return;
    }
    if (showUpsell) return;
    history.push(path.concat(pathSuffix ?? ''));
    onNavigate?.();
  };

  return (
    <Tooltip label={t(tKeys.NAV.UPSELL)} position="right" offset={20} disabled={!showUpsell}>
      <NavLink
        onClick={handleClick}
        active={doesMatchCurrentPath}
        className={styles.navLink}
        classNames={{
          root: styles.navLinkRoot(showUpsell),
        }}
        label={<Text fw={doesMatchCurrentPath ? 500 : undefined}>{label}</Text>}
        leftSection={
          <Icon
            style={{ opacity: showUpsell ? '0.4' : '' }}
            color={theme.colors.purple[5]}
            stroke="1.7"
            size={20}
          />
        }
        rightSection={
          <MenuItemBadges unreadCount={unreadCount} tagLabel={tagLabel} showUpsell={showUpsell} />
        }
      />
    </Tooltip>
  );
};

export const styles = {
  navLinkRoot: (isDimmed = false) => css`
    box-sizing: border-box;
    border-radius: 8px;

    ${
      isDimmed
        ? `
      .mantine-NavLink-label {
        opacity: 0.4;
      }
      `
        : ''
    }
  `,
  navLink: css`
    font-weight: 400;
    margin-bottom: 4px;
  `,
};
